// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

/**
 * Safely formats a number to a fixed decimal place, handling null/undefined values
 * @param value - The number to format (can be null or undefined)
 * @param decimalPlaces - Number of decimal places (default: 1)
 * @param fallback - Fallback string when value is null/undefined (default: 'N/A')
 * @returns Formatted string
 */
export function formatNumber(value: number | null | undefined, decimalPlaces = 1, fallback = 'N/A'): string {
  if (value === null || value === undefined || isNaN(value)) {
    return fallback;
  }
  return value.toFixed(decimalPlaces);
}

/**
 * Safely formats a percentage value, handling null/undefined values
 * @param value - The percentage value to format (0-100)
 * @param decimalPlaces - Number of decimal places (default: 0)
 * @param fallback - Fallback string when value is null/undefined (default: 'N/A')
 * @returns Formatted string with % symbol
 */
export function formatPercentage(value: number | null | undefined, decimalPlaces = 0, fallback = 'N/A'): string {
  if (value === null || value === undefined || isNaN(value)) {
    return fallback;
  }
  return `${value.toFixed(decimalPlaces)}%`;
}

/**
 * Safely formats a delta value with directional arrow
 * @param value - The delta value (can be positive, negative, or zero)
 * @param decimalPlaces - Number of decimal places (default: 1)
 * @param showSign - Whether to show + or - sign (default: false, uses arrows instead)
 * @returns Formatted string with arrow or sign prefix
 */
export function formatDelta(value: number | null | undefined, decimalPlaces = 1, showSign = false): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }

  const absValue = Math.abs(value);
  const formattedValue = absValue.toFixed(decimalPlaces);

  if (value > 0) {
    return showSign ? `+${formattedValue}` : `↑ ${formattedValue}`;
  } else if (value < 0) {
    return showSign ? `-${formattedValue}` : `↓ ${formattedValue}`;
  } else {
    return formattedValue;
  }
}

/**
 * Safely gets the sign symbol for a delta value
 * @param value - The delta value
 * @returns Arrow symbol (↑, ↓) or empty string for zero/null
 */
export function getDeltaSign(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  if (value > 0) return '↑';
  if (value < 0) return '↓';
  return '';
}

/**
 * Safely formats a file size in bytes to a human-readable format
 * @param bytes - File size in bytes
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns Formatted string with appropriate unit (KB, MB, GB)
 */
export function formatFileSize(bytes: number | null | undefined, decimalPlaces = 2): string {
  if (bytes === null || bytes === undefined || isNaN(bytes) || bytes < 0) {
    return 'Unknown';
  }

  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;

  if (bytes >= GB) {
    return `${(bytes / GB).toFixed(decimalPlaces)} GB`;
  } else if (bytes >= MB) {
    return `${(bytes / MB).toFixed(decimalPlaces)} MB`;
  } else if (bytes >= KB) {
    return `${(bytes / KB).toFixed(decimalPlaces)} KB`;
  } else {
    return `${bytes} bytes`;
  }
}
